import styled from '@emotion/styled';
import {
  CustomTabStore,
  DownloadLinkTypeStore,
  GroupStore,
  MembershipGroupStore,
  PodcastStore,
  SiteStore,
} from '@seedlang/stores';
import { isBlank } from '@seedlang/utils';
import { AppUI } from '@seedlang/state';
import autobind from 'autobind-decorator';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceImageUpload from 'components/form/in_place_image_upload';
import InPlaceText from 'components/form/in_place_text';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import InfoTooltip from 'components/info_tooltip';
import Text from 'components/text';
import Cookies from 'js-cookie';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import BackEndExplainer from 'pages/creator/back_end_explainer';
import React from 'react';
import Stack, { gaps } from 'components/layout/stack';
import Spinner from 'components/spinner';
import Grid from 'components/layout/grid';
import PageTitle from 'components/common/page_title';
import { Link } from 'react-router';
import Alert from 'components/alert';

const Wrapper = styled(Stack)`
  gap: ${gaps.sm};
  align-items: stretch;
  .small {
    font-size: 12px;
    line-height: 14px!important;
  }
`;

@observer
class WorksheetSettings extends React.Component {
  constructor(props) {
    super(props);
    this.getSite();
    if (!AppUI.membershipTypeStore.hasIndexData) {
      AppUI.membershipTypeStore.getIndex();
    }
    if (!MembershipGroupStore.hasIndexData) {
      MembershipGroupStore.getIndex();
    }
    if (!DownloadLinkTypeStore.hasIndexData) {
      DownloadLinkTypeStore.getIndex({});
    }
    if (!PodcastStore.hasIndexData) {
      PodcastStore.getIndex({});
    }
    if (!CustomTabStore.hasIndexData) {
      CustomTabStore.getIndex({});
    }
  }

  componentDidMount() {
    this.getSite();
  }

  componentDidUpdate(prevProps) {
    if (this.props.routeParams.conceptId !== prevProps.routeParams.conceptId) {
      this.getSite();
    }
  }

  @computed get site() {
    return SiteStore.showData;
  }

  @autobind getSite() {
    SiteStore.getShow({ ids: { siteId: Cookies.get('site_id') }}, this.afterGetShow);
  }

  @autobind afterGetShow(resp) {
    SiteStore.setShowData(resp);
  }

  @autobind loadGroup() {
    GroupStore.getShow({ids: {groupId: this.props.params.groupId}});
  }

  @autobind onUpdateExerciseAccessMembershipType(membershipTypeId) {
    const membershipType = AppUI.membershipTypeStore.indexData.find(mt => mt.id === membershipTypeId);
    if (membershipType) {
      AppUI.membershipTypeStore.update({
        ids: { membershipTypeId },
        data: { canAccessExercises: !membershipType.canAccessExercises },
      }, () => AppUI.membershipTypeStore.getIndex());
    }
  }

  @autobind onUpdateVocabAccessMembershipType(membershipTypeId) {
    const membershipType = AppUI.membershipTypeStore.indexData.find(mt => mt.id === membershipTypeId);
    if (membershipType) {
      AppUI.membershipTypeStore.update({
        ids: { membershipTypeId },
        data: { canAccessVocab: !membershipType.canAccessVocab },
      }, () => AppUI.membershipTypeStore.getIndex());
    }
  }

  render() {
    if (!SiteStore.hasShowData) {
      return <Spinner />;
    }

    return (
      <Wrapper>
        <PageTitle>Post Settings</PageTitle>
        <BackEndExplainer>These settings affect the way posts are displayed to users.</BackEndExplainer>
        <InPlaceCheckbox
          legend='General Post Settings'
          id={this.site.id}
          value={this.site.showWorksheetComments}
          model='sites'
          field='show_worksheet_comments'
        >
          Show Post Comments <InfoTooltip position='left'>Users will be able to comment on posts and exercises.</InfoTooltip>
        </InPlaceCheckbox>
        <div className='backend-title'>Post Vocab Settings</div>
        <Grid columns={3}>
          <fieldset>
            <legend>Vocab settings</legend>
              <div>
                <InPlaceCheckbox
                  id={this.site.id}
                  value={this.site.showWordPages}
                  model='sites'
                  field='show_word_pages'
                  disabled={!this.site.language?.hasLessons}
                >
                  Link to Word Pages (with Video)
                  <InfoTooltip>
                    <div>If enabled, users can click on words to open up a page</div>
                    <div>with information about the word and a video (if available).</div>
                    {
                      !this.site.language?.hasLessons &&
                        <div className='small'>This is disabled for languages currently without any videos.</div>
                    }
                  </InfoTooltip>
                </InPlaceCheckbox>
              </div>
              <div>
                <InPlaceCheckbox
                  id={this.site.id}
                  value={this.site.showWordStress}
                  model='sites'
                  field='show_word_stress'
                >
                  Word Stress <InfoTooltip><div>By toggling this, you will have the ability</div><div>to underline characters in words to indicate stress</div></InfoTooltip>
                </InPlaceCheckbox>
              </div>
              <div>
                <InPlaceCheckbox
                  id={this.site.id}
                  value={this.site.showPlural}
                  model='sites'
                  field='show_plural's
                >
                  Show Plural <InfoTooltip>The plural of each noun will be displayed next to the singular form.</InfoTooltip>
                </InPlaceCheckbox>
              </div>
              <div>
                <InPlaceCheckbox
                  model='sites'
                  field='show_alternate_target_text'
                  value={this.site.showAlternateTargetText}
                  id={this.site.id}
                  afterChange={this.getSite}
                >
                  Show Romanized spelling <InfoTooltip>Useful for languages that use several writing systems.</InfoTooltip>
                </InPlaceCheckbox>
              </div>
              {
              this.site.showAlternateTargetText &&
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Text
                    fontSize='12px'
                    wrap='nowrap'
                    margin='0 5px 0 0'
                  >
                    Column Name:
                  </Text>
                  <InPlaceText
                    submitOnEnter
                    id={this.site.id}
                    defaultValue={this.site.alternateTargetTextName}
                    model='sites'
                    field='alternate_target_text_name'
                    show={isBlank(this.site.alternateTargetTextName)}
                    afterChange={this.getSite}
                    fontSize='12px'
                  />
                  {
                    isBlank(this.site.alternateTargetTextName) &&
                      <i className='fa fa-warning' />
                  }
                </div>
            }
              <div>
                <InPlaceCheckbox
                  value={this.site.hideDefiniteArticle}
                  model='sites'
                  field='hide_definite_article'
                  id={this.site.id}
                  afterChange={this.getSite}
                >
                  Hide Articles & Show Gender
                  <InfoTooltip>
                    <div>By selecting this option, articles will no longer be displayed before nouns,</div>
                    <div>and gender abbreviations will be added after nouns (if relevant).</div></InfoTooltip>
                </InPlaceCheckbox>
              </div>
              <InPlaceCheckbox
                model='sites'
                field='show_level_column'
                value={SiteStore.showData.showLevelColumn}
                id={SiteStore.showData.id}
              >
                Show level column <InfoTooltip>The level of each word (A1, A2, etc.) will be displayed.</InfoTooltip>
              </InPlaceCheckbox>
          </fieldset>
          <fieldset>
            <legend>Filter settings <InfoTooltip>Users can filter the words by...</InfoTooltip></legend>
              <InPlaceCheckbox
                model='sites'
                field='show_level_filter'
                value={SiteStore.showData.showLevelFilter}
                id={SiteStore.showData.id}
              >
                Show level filters
              </InPlaceCheckbox>
              <InPlaceCheckbox
                model='sites'
                field='show_word_type_filter'
                value={SiteStore.showData.showWordTypeFilter}
                id={SiteStore.showData.id}
              >
                Show word type filters
              </InPlaceCheckbox>
          </fieldset>
          <Stack as='fieldset'>
            <legend>Sorting Settings <InfoTooltip>Users can sort the words by...</InfoTooltip></legend>
              <InPlaceCheckbox
                model='sites'
                field='show_alphabetical_sort'
                value={SiteStore.showData.showAlphabeticalSort}
                id={SiteStore.showData.id}
              >
                Show alphabetical sort
              </InPlaceCheckbox>
              <InPlaceCheckbox
                model='sites'
                field='show_chronological_sort'
                value={SiteStore.showData.showChronologicalSort}
                id={SiteStore.showData.id}
              >
                Show chronological sort
              </InPlaceCheckbox>
              <InPlaceCheckbox
                model='sites'
                field='show_level_sort'
                value={SiteStore.showData.showLevelSort}
                id={SiteStore.showData.id}
              >
                Show level sort
              </InPlaceCheckbox>
              {
                this.site.language?.hasLessons &&
                  <InPlaceCheckbox
                    model='sites'
                    field='show_frequency_sort'
                    value={SiteStore.showData.showFrequencySort}
                    id={SiteStore.showData.id}
                  >
                    Show frequency sort
                  </InPlaceCheckbox>
              }
          </Stack>
        </Grid>
        <div className='backend-title'>Video Post Access Settings</div>
        <Alert background='gray'>For podcast posts, only users with access to the podcast have access to the vocab and exercises. See <Link to='/creator/podcasts'>Podcast settings</Link> to set access.</Alert>
        <Grid columns={2}>
          <Stack gap='xs' as='fieldset'>
            <legend>Vocab Access Membership Types</legend>
            {AppUI.membershipTypeStore.indexData.map(item => (
              <div key={item.id}>
                <InPlaceCheckbox
                  value={item.canAccessVocab}
                  onChange={() => this.onUpdateVocabAccessMembershipType(item.id)}
                >
                  {item.name}
                </InPlaceCheckbox>
              </div>
            ))}
          </Stack>
            <Stack gap='xs' as='fieldset'>
              <legend>Exercise Access Membership Types</legend>
              {AppUI.membershipTypeStore.indexData.map(item => (
                <div key={item.id}>
                  <InPlaceCheckbox
                    value={item.canAccessExercises}
                    onChange={() => this.onUpdateExerciseAccessMembershipType(item.id)}
                  >
                    {item.name}
                  </InPlaceCheckbox>
                </div>
              ))}
            </Stack>
        </Grid>
        <div className='backend-title'>PDF Export Settings</div>
        <fieldset>
          <legend>PDF Banner Image (1160x230) <InfoTooltip>This is the image that will be displayed in the PDF version of the post.</InfoTooltip></legend>
          <InPlaceImageUpload
            model='images'
            image={GroupStore.showData.image}
            onChange={() => GroupStore.reloadShow()}
            querystring={{
              imageable_id: GroupStore.showData.id,
              imageable_type: 'Group',
            }}
          />
        </fieldset>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(WorksheetSettings);
